/**
 * studentAwardWins/{id} - the PUBLIC face of an approved monthly award.
 *
 * WHY THIS COLLECTION EXISTS
 * --------------------------
 * The first cut of the Shout-out Wall let families read studentAwardNominations
 * directly whenever status == 'approved'. Firestore read rules cannot hide
 * fields, so that handed every family the whole nomination: the `note` a
 * teacher wrote, the nominator, the reviewer, every timestamp. Every one of
 * those notes was solicited as "add the note Kim should see before approving"
 * (pages/StudentAwards.tsx) and "a short note for Kim"
 * (netlify/functions/studentAwardsReminder.mjs).
 *
 * Bobbie, 2026-08-05: "Families see NAMES AND AWARDS ONLY. The nomination
 * notes stay private." So a card is the dancer, the award, the month, the
 * moment it went up, and since 2026-09-29 `fromName`, the display name of who
 * made the pick. THERE IS NO note FIELD AND NO NOMINATOR UID, on purpose.
 * Adding either publishes staff-confidential writing to the whole studio,
 * which is the exact thing this collection exists to prevent.
 *
 * ONLY THE SERVER WRITES IT (Bobbie, 2026-09-29: "for the award section, I
 * think it should refuse anything that doesn't come from the green put on the
 * wall button"). The green button calls netlify/functions/studentAwardAction.mjs
 * publishMonthWall, which writes every card with the Admin SDK in the shape
 * lib/awardWinCard.ts defines, and shared/firestore.rules refuses every client
 * create and update. This file only reads; a Director's Take down deletes
 * (lib/studentAwards.ts takeDownAwardWin). The doc id is built from the three
 * public facts, so one dancer, one award, one month is one card, and a second
 * tap of the wall step duplicates nothing.
 *
 * AND ONLY THE WALL BUTTON'S CARDS ARE READ (the same ruling). Cards the old
 * approve flow left behind carry no `postedVia`, and every hook here drops
 * them, for every viewer, a Director included (keepWallButtonCards in
 * lib/awardWinCard.ts). The single exception is the Director's Student Awards
 * page, which asks for them by name (useSchoolYearAwardWins with
 * includeOldFlow) to list them apart for Kim to stamp or take down.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { StudentAwardType } from './studentAwards';
import { cleanFromName, schoolYearKeyForPeriod, studioMonthKey, type WallAwardType } from './shoutoutWall';
import { useUserDoc } from './users';
import { canSeeAwardWins } from './wallPause';
import { keepWallButtonCards } from './awardWinCard';

export { awardWinDocId, AWARD_WIN_LABELS, type AwardWinType } from './awardWinCard';

export interface StudentAwardWinDoc {
  id: string;
  periodKey: string;
  schoolYearKey: string;
  /**
   * Student of the Month or Monthly Spotlight, and since 2026-09-29 also
   * 'jr-staff-mvp' at runtime: the wall step puts the month's Jr Staff MVP up
   * with the winners (lib/awardWinCard.ts AwardWinType). Typed narrow so the
   * pages keyed on STUDENT_AWARD_LABELS keep compiling; a reader that looks a
   * label up checks the type first, or falls back.
   */
  awardType: StudentAwardType;
  studentId: string;
  studentName: string;
  /**
   * Who made the pick, a display name, never an email ("Ms. Tara", or "A and B"
   * when two picked). Absent on the MVP card and on cards put up before
   * 2026-09-29. The wall reads it defensively (cleanFromName) and shows
   * "from (name)" only when it is here. A NAME and nothing else: still no note,
   * no nominator uid, no reviewer.
   */
  fromName?: string;
  /** 'wall-button' on every card the wall step wrote (lib/awardWinCard.ts); absent on cards the old approve flow wrote. */
  postedVia?: 'wall-button';
  /** When the wall step put it up. */
  postedAt?: Timestamp | FieldValue;
  announcedAt: Timestamp | FieldValue;
}

/**
 * Any card the Shout-out Wall can hold: the two classic awards plus the Jr
 * Staff MVP's card (`jr-staff-mvp`), which the wall step writes when a Director
 * puts the month's winners on the wall (lib/awardWinCard.ts). The wall reads it
 * as possibly absent and falls back to "Coming soon".
 *
 * A separate type on purpose. Every other reader of the wins (the roster
 * award pills, the Student Awards page) is typed for the two classic awards
 * and indexes label tables with them, so they keep getting StudentAwardWinDoc
 * (see useSchoolYearAwardWins) and never meet a card kind they cannot label.
 */
export interface WallCardDoc extends Omit<StudentAwardWinDoc, 'awardType'> {
  awardType: WallAwardType;
}

function isClassicAward(card: WallCardDoc): card is WallCardDoc & { awardType: StudentAwardType } {
  return card.awardType === 'student-of-month' || card.awardType === 'jr-spotlight';
}

/**
 * One wall card as the readers see it. The stored doc is trusted for its three
 * public facts and nothing more: `fromName` is the only optional extra, and it
 * is scrubbed on the way in so a blank, non-text or email-shaped value never
 * reaches a screen.
 */
export function readWallCard(id: string, data: Record<string, unknown>): WallCardDoc {
  const { fromName: rawFromName, ...rest } = data;
  const fromName = cleanFromName(rawFromName);
  return {
    id,
    ...(rest as Omit<WallCardDoc, 'id' | 'fromName'>),
    ...(fromName ? { fromName } : {}),
  };
}

export interface UseStudentAwardWinsResult {
  loading: boolean;
  wins: StudentAwardWinDoc[];
  error: string | null;
}

export interface UseWallCardsResult {
  loading: boolean;
  cards: WallCardDoc[];
  error: string | null;
}

/**
 * The one gated reader behind both wall-card hooks. While award winners are
 * hidden until Kim posts them (lib/wallPause.ts), only a Director reads them
 * (Bobbie, 2026-09-29: "the awards should not go live until she posts on the
 * wall"). Gated HERE, in the hook, not in the page, so no caller can read a
 * wall card past the pause: the wall, the stars and pills (through
 * useSchoolYearAwardWins below, which is built on this) and the Monthly
 * recipients card. It also keeps only the cards the wall button wrote. A
 * viewer who may not see them has no listener at all, so nothing is downloaded
 * into the browser.
 *
 * Bounded by a single-field EQUALITY filter, which Firestore maintains its own
 * index for: no composite index, and no "download every award ever written" as
 * the years pile up.
 */
function useGatedWallCards(
  field: 'schoolYearKey' | 'periodKey',
  value: string,
  enabled: boolean,
  includeOldFlow = false
): UseWallCardsResult {
  const { userDoc } = useUserDoc();
  const allowed = canSeeAwardWins(userDoc?.role ?? null);
  const live = enabled && allowed;
  const [cards, setCards] = useState<WallCardDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!live || !db || !value) {
      setCards([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(collection(db, 'studentAwardWins'), where(field, '==', value));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setCards(snap.docs.map((d) => readWallCard(d.id, d.data())));
        setLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[studentAwardWins] wall card listener failed', err);
        setError("Couldn't load this year's monthly recipients right now. Try again in a moment.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [live, field, value]);

  // ONLY the wall button's cards, for everyone: a card the old approve flow left
  // behind (no postedVia) is refused here, so nothing that reads through this
  // hook can draw or count it. Only the Director's Student Awards page asks to
  // see them (includeOldFlow), to list them apart.
  const visible = useMemo(
    () => (includeOldFlow ? cards : keepWallButtonCards(cards)),
    [cards, includeOldFlow]
  );

  // A stable empty list while hidden, so a memo keyed on it never re-runs.
  return { loading, cards: allowed ? visible : HIDDEN_CARDS, error: allowed ? error : null };
}

/** A stable empty list for a viewer who may not see award cards yet. */
const HIDDEN_CARDS: WallCardDoc[] = [];

/**
 * One school year of EVERY wall card, the Jr Staff MVP's included. The
 * Shout-out Wall reads this one (and the one-month reader below).
 */
export function useSchoolYearWallCards(
  schoolYearKey: string,
  enabled = true
): UseWallCardsResult {
  return useGatedWallCards('schoolYearKey', schoolYearKey, enabled);
}

/**
 * ONE month of wall cards, by `periodKey`, behind the same gate. What the wall
 * reads for last month when it sits in the school year before (every August it
 * is July), so it never downloads that whole year to show one month.
 */
export function useMonthWallCards(periodKey: string, enabled = true): UseWallCardsResult {
  return useGatedWallCards('periodKey', periodKey, enabled);
}

/**
 * One school year of published Student of the Month and Monthly Spotlight
 * wins: the two classic awards and nothing else, so every caller that labels
 * a win from a two-entry table keeps working when the wall also holds a Jr
 * Staff MVP card. The same one gated listener as useSchoolYearWallCards,
 * narrowed, so it shows only cards the wall button wrote.
 *
 * `includeOldFlow` is for ONE caller: the Director's Student Awards page, which
 * lists the cards the old approve flow left (no postedVia) apart, under "Left by
 * the old approve flow", so Kim can stamp them or take them down
 * (lib/awardWallState.ts). Everyone else leaves it off and never sees one.
 */
export function useSchoolYearAwardWins(
  schoolYearKey: string,
  enabled = true,
  options: { includeOldFlow?: boolean } = {}
): UseStudentAwardWinsResult {
  const { loading, cards, error } = useGatedWallCards('schoolYearKey', schoolYearKey, enabled, options.includeOldFlow === true);
  const wins = useMemo(() => cards.filter(isClassicAward), [cards]);
  return { loading, wins, error };
}

export interface UseCurrentMonthAwardWinsResult {
  loading: boolean;
  /** The current studio-time 'YYYY-MM' period. Exposed so a caller can pass
   * it straight to currentMonthAwardTypesFor (lib/rosterStatusPills.ts)
   * without recomputing "now" a second time and risking the two clocks
   * drifting a tick apart. */
  currentPeriodKey: string;
  /** Every published win in the CURRENT SCHOOL YEAR, not yet narrowed to
   * this month - narrowing per dancer is currentMonthAwardTypesFor's job,
   * against currentPeriodKey above. Student of the Month and Spotlight of
   * the Month only. */
  wins: StudentAwardWinDoc[];
  /** The Jr Staff MVP's wall cards for the CURRENT MONTH only, read from the
   * same one gated listener as `wins` (Director-only while the awards are
   * hidden, the wall button's cards only). Their studentId is her dancer
   * record or, with none, her person uid (lib/awardWinCard.ts). Read by the
   * attendance award pills (lib/attendanceAwardPills.ts); the stars do not
   * use it, so nothing else shows the MVP any earlier or to anyone new. */
  mvpCards: WallCardDoc[];
  error: string | null;
}

/**
 * This month's published wins, studio-wide - backs the celebratory shoutout
 * pill on the Attendance and Teach class rosters. Bobbie: "maybe flag if it
 * they are the spotlight student of the month or student of the month? To
 * give them a shoutout in class?"
 *
 * ONE listener (task law: "one listener for the roster, not one per
 * dancer"), built on useSchoolYearAwardWins - the same "this month" idiom
 * ShoutoutWall.tsx already uses (currentPeriodKey = studioMonthKey()).
 *
 * Public congratulations, so this stays enabled for every staff viewer who
 * reaches a roster, Jr Staff included: the deployed studentAwardWins read
 * rule is `isActiveUserByDoc()`, no role narrower than that (see
 * shared/firestore.rules), unlike injuryNotes below it in this file's
 * sibling module.
 */
export function useCurrentMonthAwardWins(enabled = true): UseCurrentMonthAwardWinsResult {
  // The stars and award pills this feeds are hidden with the award cards, for
  // everyone but a Director, by the gate in useGatedWallCards.
  const currentPeriodKey = useMemo(() => studioMonthKey(), []);
  const currentSchoolYear = useMemo(
    () => schoolYearKeyForPeriod(currentPeriodKey),
    [currentPeriodKey]
  );
  // The gated school-year reader with EVERY card kind, the same single
  // listener useSchoolYearAwardWins is built on (its only difference is
  // narrowing to the two classic awards), so the MVP's card rides along with
  // no second read and no new visibility.
  const { loading, cards, error } = useSchoolYearWallCards(currentSchoolYear, enabled);
  const wins = useMemo(() => cards.filter(isClassicAward), [cards]);
  const mvpCards = useMemo(
    () => cards.filter((card) => card.awardType === 'jr-staff-mvp' && card.periodKey === currentPeriodKey),
    [cards, currentPeriodKey]
  );
  return { loading, currentPeriodKey, wins, mvpCards, error };
}
